import type { GridMatch, SharedGrid } from '@/types/tournament.model';
import { formatDateForUser } from '@/utils/formatters';
import {
  effectiveCell,
  teamLabel,
  tournamentOrderOf,
  type PlaceholderLabelMap,
} from './grid-model';
import { sortForCourts } from './court-model';

const HEAD = [
  [
    'Hora',
    'Cancha',
    'Club',
    'Torneo',
    'Grupo/Instancia',
    'Equipo A',
    'Equipo B',
    'Resultado',
  ],
];

/** One (club, date, time) bucket: everything a court number is scoped to, in one place. */
type Group = {
  date: string;
  time: string;
  clubId: string | null;
  matches: GridMatch[];
};

type Row = {
  date: string;
  time: string;
  court: number;
  clubName: string;
  clubRank: number;
  tournamentName: string;
  stage: string;
  team1: string;
  team2: string;
};

/**
 * Flattens the grid into printable rows.
 *
 * Deliberately does NOT reuse `buildGrid`: that model is per club, and a match with a null
 * `clubId` is included in *every* club's model on purpose (so it stays visible somewhere), which
 * means concatenating one model per club would print those matches once per club. This walks
 * `grid.matches` once instead, which is also what lets a single document cover every club — the
 * whole point of the Club column.
 *
 * Court numbers are derived inside each `(club, date, time)` group with the same `sortForCourts`
 * the day view uses, so the paper and the screen agree.
 */
function buildRows(
  grid: SharedGrid,
  moves: Map<string, string>,
  placeholderLabels: PlaceholderLabelMap,
): Row[] {
  // Shared with the screen so court numbers can't drift between paper and display.
  const tournamentOrder = tournamentOrderOf(grid.tournaments);
  // The explicit generics matter: `new Map(xs.map(x => [a, b]))` infers the inner arrays as
  // `(string | number)[]`, not as tuples, and the Map constructor then rejects them.
  const tournamentNames = new Map<string, string>(
    grid.tournaments.map((t) => [t.id, t.name]),
  );
  const clubNames = new Map<string, string>(grid.clubs.map((c) => [c.id, c.name]));
  const clubRanks = new Map<string, number>(grid.clubs.map((c, i) => [c.id, i]));

  // Group by (club, date, time): that triple is what a court number is scoped to. The position
  // travels inside the group rather than in a parallel map keyed the same way — one structure
  // means there is no second place to remember to update.
  const groups = new Map<string, Group>();

  grid.matches.forEach((match) => {
    const { date, time } = effectiveCell(match, moves);
    const key = `${match.clubId ?? ''}|${date}|${time}`;
    const group = groups.get(key);
    if (group) group.matches.push(match);
    else groups.set(key, { date, time, clubId: match.clubId, matches: [match] });
  });

  const rows: Row[] = [];
  groups.forEach(({ date, time, clubId, matches }) => {
    sortForCourts(matches, tournamentOrder).forEach((match, i) => {
      rows.push({
        date,
        time,
        court: i + 1,
        // A match with no club, or with a club outside the grid, prints an empty Club cell —
        // same rule as every other value we don't have.
        clubName: (clubId && clubNames.get(clubId)) || '',
        // Sorts unknown clubs last without hiding them.
        // Ternary rather than `clubId && …`: with `clubId: string | null`, `&&` narrows the
        // falsy branch to `"" | null`, so `?? MAX` leaves an unreachable-but-real `""` in the
        // inferred type (strict mode rejects it as a non-number). The ternary sidesteps that.
        clubRank: (clubId ? clubRanks.get(clubId) : undefined) ?? Number.MAX_SAFE_INTEGER,
        tournamentName: tournamentNames.get(match.tournamentId) ?? '',
        // Already resolved by the backend to "Grupo B" or the round's own name ("Semifinal"),
        // the same string the cards show, so paper and screen name a match the same way.
        stage: match.stage,
        // A bracket slot still waiting on the group stage prints which qualifier fills it — on
        // paper that is the difference between a usable sheet and one row of "Equipo 1" per match.
        team1: teamLabel(
          match.team1,
          'Equipo 1',
          placeholderLabels.get(match.matchId)?.team1,
        ),
        team2: teamLabel(
          match.team2,
          'Equipo 2',
          placeholderLabels.get(match.matchId)?.team2,
        ),
      });
    });
  });

  return rows.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      a.time.localeCompare(b.time) ||
      a.clubRank - b.clubRank ||
      a.court - b.court,
  );
}

/**
 * Builds the printable sheet and hands it to the browser as a download.
 *
 * One page per day: a day that overflows keeps going onto extra pages by itself, and the next
 * day still starts clean. That matches how the sheet gets used — one page per day, on a table.
 *
 * `jspdf` is loaded on demand: it is far too big for the initial bundle, and this runs at most
 * once per visit (`.claude/rules/performance.md`).
 *
 * The `Resultado` column is always empty. `GridMatchDTO` carries `hasResult` but not the score,
 * so there is nothing to print even for a match already played — which suits a sheet meant to be
 * filled in by hand, but is why it stays blank rather than being conditionally populated.
 */
export async function exportGridPdf(
  grid: SharedGrid,
  moves: Map<string, string>,
  placeholderLabels: PlaceholderLabelMap,
): Promise<void> {
  const rows = buildRows(grid, moves, placeholderLabels);
  if (rows.length === 0) return;

  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);

  const doc = new jsPDF({ orientation: 'landscape' });
  const anchor = grid.tournaments.find((t) => t.current);
  const title = anchor?.name ?? 'Grilla';

  const dates = [...new Set(rows.map((r) => r.date))];

  dates.forEach((date, i) => {
    if (i > 0) doc.addPage();

    doc.setFontSize(14);
    doc.text(title, 14, 15);
    doc.setFontSize(11);
    doc.text(formatDateForUser(date, "EEEE d 'de' MMMM"), 14, 22);

    autoTable(doc, {
      head: HEAD,
      body: rows
        .filter((r) => r.date === date)
        .map((r) => [
          r.time,
          String(r.court),
          r.clubName,
          r.tournamentName,
          r.stage,
          r.team1,
          r.team2,
          '',
        ]),
      startY: 28,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [30, 41, 59] },
      // The result is written by hand on paper, so it needs room even though it prints empty.
      columnStyles: { 7: { cellWidth: 40 } },
    });
  });

  doc.save(`grilla-${slug(title)}.pdf`);
}

function slug(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'torneo';
}
